// predefined dialogs configuration
import Vue from 'vue'
import queue from 'async/queue'

const DIALOGS_TYPES = {
  'upload-files': {
    title: 'Upload files',
    body: require('~/components/common/dialog/types/DialogUploadFiles.vue').default
  },
  'single-string-input': {
    body: require('~/components/common/dialog/types/DialogSingleStringInput.vue').default,
    width: 480
  },
  'ascires-form': {
    body: require('~/components/common/dialog/types/DialogAsciresForm.vue').default,
    width: 1200,
    persistent: true
  },
  'terms-and-privacy': {
    body: require('~/components/common/dialog/types/DialogTermsAndPolicy.vue').default,
    fillHeight: true,
    contentClass: 'pr-0 pl-3'
  },
  'share': {
    title: require('~/components/common/dialog/types/DialogShareFileHeader.vue').default,
    body: require('~/components/common/dialog/types/DialogNewShareFile.vue').default,
    width: '800px',
    persistent: true,
    contentClass: 'shareDialogCardText',
    actions: {
      cancel: 'Close'
    }
  },
  'share-email': {
    title: 'Send Magic Link through email',
    body: require('~/components/common/dialog/types/DialogShareMagicLinkEmail.vue').default,
    persistent: true,
    actions: {
      cancel: 'Close',
      accept: 'Send'
    }
  },
  'account-verification': {
    title: 'Account verification',
    body: require('~/components/common/dialog/types/DialogAccountVerification.vue').default,
    persistent: true
  },
  'contract': {
    body: require('~/components/common/dialog/types/DialogContract.vue').default,
    fillHeight: true,
    persistent: true,
    contentClass: 'pr-0 pl-3'
  }
}

const EMPTY_DIALOG = {
  title: null,
  body: null,
  width: 720,
  contentClass: null,
  fillHeight: false,
  persistent: false,
  loading: false,
  checkbox: null
}

var closingPromises = []

function initQueue () {
  return queue(function (task, callback) {
    let err
    let resp
    task.do().then((response) => {
      resp = response
    }).catch(e => {
      console.log(e)
    }).finally(() => {
      return callback(err, resp)
    })
  })
}

export default {
  namespaced: true,
  state: {
    dialog: EMPTY_DIALOG,
    data: null,
    visible: false,
    queue: [],
    taskQueue: initQueue(),
    closed: true,
    valid: true,
    showed: false,
    lastAnswer: null
  },
  mutations: {
    setDialog (state, dialog) {
      state.valid = true
      state.dialog = dialog
    },
    setVisible (state, visible) {
      state.visible = visible
    },
    setClosed (state, closed) {
      state.closed = closed
    },
    setData (state, data) {
      state.data = data
    },
    setProps (state, props) {
      if (state.dialog) {
        state.dialog.props = props
      }
    },
    setTitle (state, title) {
      state.dialog = {...state.dialog, title}
    },
    setLoading (state, loading) {
      state.dialog = {...state.dialog, loading}
    },
    setValid (state, isValid) {
      state.valid = isValid
    },
    setActions (state, payload) {
      state.dialog.actions = payload
    },
    pushDialog (state, dialog) {
      state.queue.push(dialog)
    },
    unshiftDialog (state, dialog) {
      state.queue.unshift(dialog)
    },
    deleteDialog (state, index) {
      Vue.delete(state.queue, index)
    },
    showNextDialog (state) {
      state.valid = true
      let dialog = state.queue.shift()
      state.dialog = dialog || EMPTY_DIALOG
      if (dialog) {
        state.visible = true
        state.closed = false
      }
    },
    clearQueue (state) {
      state.queue = []
    },
    setShowed (state, payload) {
      state.showed = payload
    },
    setLastAnswer (state, payload) {
      state.lastAnswer = payload
    }
  },
  actions: {
    queueAction ({ commit, state }, { name, action, payload }) {
      let task = {
        do: async () => {
          return action.bind(this)(payload)
        },
        name
      }
      return new Promise((resolve) => {
        state.taskQueue.push(task, (payload) => {
          resolve(payload)
        })
      }).then((result) => result)
    },
    unshiftDialog ({commit, state, dispatch}, payload) {
      let action = () => {
        payload = {
          ...EMPTY_DIALOG,
          ...(payload && payload.type ? DIALOGS_TYPES[payload.type] : {}),
          ...(payload || {})
        }
        let prevDialog = Object.assign({}, state.dialog)
        commit('setVisible', false)
        commit('setClosed', true)
        return setTimeoutAsPromise(() => {
          commit('setDialog', payload)
          commit('setVisible', true)
          commit('setClosed', false)
          if (prevDialog && prevDialog.body) {
            commit('unshiftDialog', prevDialog)
          }
        }, 510)
      }
      return dispatch('queueAction', { name: 'unshiftDialog', action: action, payload })
    },
    unshiftQueue ({commit, state}, payload) {
      commit('unshiftDialog', payload)
    },
    stashDialog ({commit, state}) {
      let prevDialog = Object.assign({}, state.closed || !state.visible ? {} : state.dialog)
      commit('setVisible', false)
      commit('setClosed', true)
      if (prevDialog && prevDialog.body) {
        commit('unshiftDialog', prevDialog)
        commit('setDialog', EMPTY_DIALOG)
        commit('setData', null)
      }
    },
    showDialog ({commit, state, dispatch}, payload) {
      let action = () => {
        payload = {
          ...EMPTY_DIALOG,
          ...(payload && payload.type ? DIALOGS_TYPES[payload.type] : {}),
          ...(payload || {})
        }

        if (!state.closed) {
          // If dialog is not closed
          // instead of showing it up, we'll add it to the queue
          commit('pushDialog', payload)
        } else {
          // Else just show it
          commit('setDialog', payload)
          commit('setVisible', true)
          commit('setClosed', false)
        }
      }
      return dispatch('queueAction', { name: 'showDialog', action: action, payload })
    },
    showNextDialog ({ commit, state, dispatch }) {
      let action = () => {
        if (state.queue.length) {
          commit('showNextDialog')
        }
      }
      return dispatch('queueAction', { name: 'showNextDialog', action: action, payload: {} })
    },
    closeDialog ({ commit, state, dispatch }, payload) {
      let action = () => {
        let { transitionDuration, callback } = payload || {}
        transitionDuration = transitionDuration || 510
        commit('setVisible', false)
        const promiseID = Symbol('id')

        const promise = new Promise(resolve => {
          setTimeout(() => {
            if (state.queue.length) {
              commit('showNextDialog')
            } else {
              commit('setClosed', true)
              commit('setDialog', EMPTY_DIALOG)
              commit('setData', null)
            }

            if (typeof callback === 'function') {
              resolve(callback())
              closingPromises = closingPromises.filter(p => p.promiseID !== promiseID)
              return
            }

            resolve()
            closingPromises = closingPromises.filter(p => p.promiseID !== promiseID)
          }, transitionDuration)
        })
        closingPromises.push({ promiseID, promise })
        return promise
      }
      return dispatch('queueAction', { name: 'closeDialog', action: action, payload })
    },
    setValid ({commit}, payload) {
      commit('setValid', payload)
    },
    setData ({commit}, payload) {
      Promise.all(closingPromises.map(p => p.promise)).then(() => {
        commit('setData', payload)
      })
    },
    setProps ({commit}, payload) {
      commit('setProps', payload)
    },
    setTitle ({commit}, title) {
      commit('setTitle', title)
    },
    setLoading ({commit}, loading) {
      commit('setLoading', loading)
    },
    setActions ({commit}, payload) {
      commit('setActions', payload)
    },
    deleteDialog ({commit}, index) {
      commit('deleteDialog', index)
    },
    pushDialog ({commit}, index) {
      commit('pushDialog', index)
    },
    clearQueue ({commit}) {
      commit('clearQueue')
    },
    setShowed ({commit}, value) {
      commit('setShowed', value)
    },
    setLastAnswer ({commit}, value) {
      commit('setLastAnswer', value)
    }
  }
}

// TODO: move to common
export function setTimeoutAsPromise (handler, ms, bus) {
  return new Promise((resolve, reject) => {
    const timeoutID = setTimeout(() => {
      try {
        resolve(handler())
      } catch (error) {
        reject(error)
      }
    }, ms)
    if (bus) {
      bus.$emit('clear-timeout', () => {
        clearTimeout(timeoutID)
      })
    }
  })
}



// WEBPACK FOOTER //
// ./src/store/idonia-api/dialog.js